import $axios from '@/api/menu'
import { ApiResponse } from '@/api/types'
import { MenuItem, TypeUserRole, User } from '@/composables/auth/types'
import router from '@/router/router'
import { useAuthService } from '@/utils/auth'
import { useAppToast } from '@/utils/toast'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

export type GetUserResult = { ok: true; user: User } | { ok: false; unauthorized: boolean }

// Как часто перезапрашивать /auth/me при навигации (роль, меню, paid могут поменяться на сервере)
const USER_CACHE_TTL = 5 * 60 * 1000

export const useUserStore = defineStore('user', () => {
  const { showErrorToast } = useAppToast()
  const { removeToken, getToken, removeRahmatToken, getRahmatToken } = useAuthService()
  const userRole = ref<TypeUserRole | null>(null)
  const userMenu = ref<MenuItem[]>([])
  const user = ref<User>()
  // Токен и время, для которых загружен user — чтобы не дергать /auth/me на каждый переход
  let loadedForToken: string | null = null
  let loadedAt = 0

  const resetUser = () => {
    user.value = undefined
    userRole.value = null
    userMenu.value = []
    loadedForToken = null
    loadedAt = 0
  }

  const getUser = async (): Promise<GetUserResult> => {
    try {
      const { data } = await $axios.get<ApiResponse<User>>('/dashboard/auth/me')
      if (!data?.data) {
        showErrorToast('Ошибка', 'Произошла ошибка при получении данных о пользователе')
        return { ok: false, unauthorized: false }
      }

      user.value = data.data
      userRole.value = data.data.role || null
      userMenu.value = data.data.menu ?? []
      loadedForToken = getRahmatToken()
      loadedAt = Date.now()
      return { ok: true, user: data.data }
    } catch (error: any) {
      const status = error?.response?.status
      const unauthorized = status === 401 || status === 403
      // Для 401/403 тост уже показывает интерцептор axios
      if (!unauthorized) {
        showErrorToast('Ошибка', 'Произошла ошибка при получении данных о пользователе')
      }
      return { ok: false, unauthorized }
    }
  }

  const ensureUser = async (): Promise<GetUserResult> => {
    const token = getRahmatToken()
    const isFresh = Date.now() - loadedAt < USER_CACHE_TTL
    if (user.value && token && token === loadedForToken && isFresh) {
      return { ok: true, user: user.value }
    }
    return getUser()
  }

  const hasUser = computed(() => getToken())

  const logout = () => {
    removeToken()
    resetUser()
    router.push({ name: 'login' })
  }

  const logoutRahmat = () => {
    removeRahmatToken()
    resetUser()
    router.push({ name: 'login' })
  }

  return {
    logout,
    logoutRahmat,
    getUser,
    ensureUser,
    resetUser,
    hasUser,
    user,
    userMenu,
    userRole
  }
})
